import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { useNotification, NOTIFICATION } from '../components/NotificationSystem';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import { useAuth } from '../contexts/AuthContext';
import './MyBuilds.css';
import './Community.css';
import { CLASSES } from '../core/characterUtils';
import { normTagIds } from '../core/criteriaTags';
import { localGenPath } from '../utils/buildRoutes';
import Tabs from '../components/utils/Tabs';
import BuildListHeader from '../components/build/BuildListHeader';
import { CommunityBuildCard, LibraryBuildCard } from '../components/build/BuildCards';
import { CompactBuildCard } from '../components/build/CompactBuildCard';
import PageTemplate from '../components/layout/PageTemplate';
import { listLikedCommunityBuilds, toggleCommunityBuildLike } from '../services/buildApi';

const TXT_LOG_IN_TO_LIKE = { fr: 'Connecte-toi pour liker.', en: 'Log in to like.', de: 'Melde dich an, um zu liken.', es: 'Inicia sesión para dar like.', pt: 'Entre para curtir.' };
const TXT_MY_BUILDS_TABS = { fr: 'onglets mes stuffs', en: 'my builds tabs', de: 'Meine-Builds-Tabs', es: 'pestañas mis builds', pt: 'abas minhas builds' };

const MSG_CONFIRM_DELETE = { fr: 'Supprimer ce stuff ?', en: 'Delete this build?', de: 'Diesen Build löschen?', es: '¿Eliminar esta build?', pt: 'Excluir esta build?' };
const MSG_BUILD_COPIED = { fr: 'Stuff dupliqué.', en: 'Build duplicated.', de: 'Build dupliziert.', es: 'Build duplicada.', pt: 'Build duplicada.' };
const MSG_BUILD_DELETED = { fr: 'Stuff supprimé.', en: 'Build deleted.', de: 'Build gelöscht.', es: 'Build eliminada.', pt: 'Build excluída.' };
const TXT_TITLE = { fr: 'Mes stuffs', en: 'My builds', de: 'Meine Builds', es: 'Mis builds', pt: 'Minhas builds' };
const TXT_CREATE = { fr: 'Nouveau stuff', en: 'New build', de: 'Neuer Build', es: 'Nueva build', pt: 'Nova build' };
const TXT_EMPTY = { fr: 'Aucun stuff. Créez-en un pour commencer.', en: 'No builds. Create one to get started.', de: 'Keine Builds. Erstelle einen, um loszulegen.', es: 'Ninguna build. Crea una para empezar.', pt: 'Nenhuma build. Crie uma para começar.' };
const TXT_LOADING = { fr: 'Chargement des stuffs du compte...', en: 'Loading account builds...', de: 'Konto-Builds werden geladen...', es: 'Cargando las builds de la cuenta...', pt: 'Carregando as builds da conta...' };
const TXT_ACCOUNT_UNAVAILABLE = {
    fr: "Impossible de charger les stuffs du compte. Les stuffs locaux ne sont pas affichés quand tu es connecté.",
    en: 'Could not load account builds. Local builds are not shown while you are logged in.',
    de: 'Konto-Builds konnten nicht geladen werden. Lokale Builds werden nicht angezeigt, solange du angemeldet bist.',
    es: 'No se han podido cargar las builds de la cuenta. Las builds locales no se muestran mientras estás conectado.',
    pt: 'Não foi possível carregar as builds da conta. As builds locais não são exibidas enquanto você está conectado.',
};
const TXT_RETRY = { fr: 'Réessayer', en: 'Retry', de: 'Erneut versuchen', es: 'Reintentar', pt: 'Tentar novamente' };
const TXT_CREATED_TAB = { fr: 'stuffs créés', en: 'created builds', de: 'erstellte Builds', es: 'builds creadas', pt: 'builds criadas' };
const TXT_LIKED_TAB = { fr: 'stuffs likés', en: 'liked builds', de: 'gelikte Builds', es: 'builds con like', pt: 'builds curtidas' };
const TXT_LIKED_LOADING = { fr: 'Chargement des stuffs likés...', en: 'Loading liked builds...', de: 'Gelikte Builds werden geladen...', es: 'Cargando las builds con like...', pt: 'Carregando as builds curtidas...' };
const TXT_LIKED_EMPTY = { fr: 'Aucun stuff liké pour le moment.', en: 'No liked builds yet.', de: 'Noch keine gelikten Builds.', es: 'Ninguna build con like por ahora.', pt: 'Nenhuma build curtida por enquanto.' };
const TXT_LIKED_LOGIN = { fr: 'Connecte-toi pour voir tes stuffs likés.', en: 'Log in to see your liked builds.', de: 'Melde dich an, um deine gelikten Builds zu sehen.', es: 'Inicia sesión para ver tus builds con like.', pt: 'Entre para ver as suas builds curtidas.' };
const TXT_LIKED_UNAVAILABLE = { fr: 'Impossible de charger les stuffs likés.', en: 'Could not load liked builds.', de: 'Gelikte Builds konnten nicht geladen werden.', es: 'No se han podido cargar las builds con like.', pt: 'Não foi possível carregar as builds curtidas.' };
const TXT_FILTER_EMPTY = { fr: 'Aucun stuff ne correspond à ces filtres.', en: 'No builds match these filters.', de: 'Kein Build passt zu diesen Filtern.', es: 'Ninguna build coincide con estos filtros.', pt: 'Nenhuma build corresponde a estes filtros.' };
const MSG_LIKE_FAILED = { fr: 'Échec du like.', en: 'Like failed.', de: 'Like fehlgeschlagen.', es: 'No se ha podido dar like.', pt: 'Não foi possível curtir.' };
const TXT_SORT_LABEL = { fr: 'Trier par', en: 'Sort by', de: 'Sortieren nach', es: 'Ordenar por', pt: 'Ordenar por' };
const TXT_SORT_RECENT = { fr: 'récents', en: 'recent', de: 'neueste', es: 'recientes', pt: 'recentes' };
const TXT_SORT_LIKES = { fr: 'populaires', en: 'popular', de: 'beliebt', es: 'populares', pt: 'populares' };
const TXT_SORT_VIEWS = { fr: 'vues', en: 'views', de: 'Aufrufe', es: 'visitas', pt: 'visualizações' };
const TXT_RESULTS = { fr: 'résultats', en: 'results', de: 'Ergebnisse', es: 'resultados', pt: 'resultados' };
const TXT_SEARCH = { fr: 'Rechercher un stuff...', en: 'Search a build...', de: 'Build suchen...', es: 'Buscar una build...', pt: 'Buscar uma build...' };
const TXT_VIEW_MODE = { fr: 'Mode d\'affichage', en: 'Display mode', de: 'Anzeigemodus', es: 'Modo de visualización', pt: 'Modo de exibição' };
const TXT_GUEST_NOTICE = {
    fr: 'Tu n\'es pas connecté : seuls les stuffs enregistrés sur ce navigateur sont visibles ici.',
    en: 'You are not logged in: only the builds stored in this browser show up here.',
    de: 'Du bist nicht angemeldet: Hier erscheinen nur die in diesem Browser gespeicherten Builds.',
    es: 'No has iniciado sesión: aquí solo aparecen las builds guardadas en este navegador.',
    pt: 'Você não está conectado: aqui só aparecem as builds salvas neste navegador.',
};
const TXT_GUEST_LOGIN = { fr: 'Se connecter', en: 'Log in', de: 'Anmelden', es: 'Iniciar sesión', pt: 'Entrar' };

const TAB_CREATED = 'created';
const TAB_LIKED = 'liked';

const SORT_RECENT = 'recent';
const SORT_LIKES = 'likes';
const SORT_VIEWS = 'views';

const VIEW_COMPACT = 'compact';
const VIEW_FULL = 'full';
const VIEW_MODE_KEY = 'dofgen_mybuilds_view';

function readViewMode() {
    try {
        const stored = localStorage.getItem(VIEW_MODE_KEY);
        if (stored === VIEW_COMPACT || stored === VIEW_FULL) return stored;
    } catch (e) {
        // localStorage unavailable
    }
    return VIEW_COMPACT;
}

function writeViewMode(mode) {
    try {
        localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch (e) {
        // localStorage unavailable
    }
}

function generatorHref(lang) {
    return localGenPath(lang);
}

function getTxt(obj, lang) {
    return obj[lang] || obj.en || obj.fr || '';
}

function lastModMs(entry) {
    const iso = entry?.updatedAt || entry?.createdAt;
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : 0;
}

function compareBuilds(a, b, sortBy) {
    if (sortBy === SORT_LIKES) {
        const d = Number(b?.metrics?.likes || 0) - Number(a?.metrics?.likes || 0);
        if (d !== 0) return d;
    } else if (sortBy === SORT_VIEWS) {
        const d = Number(b?.metrics?.views || 0) - Number(a?.metrics?.views || 0);
        if (d !== 0) return d;
    }
    // Default sort and tiebreaker: most recently modified first.
    return lastModMs(b) - lastModMs(a);
}

const MyBuilds = ({ lang = 'fr' }) => {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { isAuthenticated, authState, startLogin } = useAuth();
    const {
        builds,
        activeBuildId,
        storageMode,
        openBuild,
        createBuild,
        deleteBuild,
        duplicateBuild,
        refreshBuilds,
        getBuildPath,
        patchBuildsEntry,
    } = useGeneratorContext();
    const isAuthLoading = authState === 'loading';
    const accountBuildsUnavailable = isAuthenticated && storageMode === 'offline-auth-fallback';
    const [activeTab, setActiveTab] = useState(TAB_CREATED);
    const visibleBuilds = useMemo(
        () => {
            if (isAuthLoading || accountBuildsUnavailable) return [];
            return isAuthenticated ? builds.filter(entry => entry?.scope === 'remote') : builds;
        },
        [accountBuildsUnavailable, builds, isAuthLoading, isAuthenticated]
    );
    const [likedBuilds, setLikedBuilds] = useState([]);
    const [likedLoading, setLikedLoading] = useState(false);
    const [likedLoadFailed, setLikedLoadFailed] = useState(false);

    const [selectedClasses, setSelectedClasses] = useState(new Set());
    const [selectedTags, setSelectedTags] = useState(new Set());
    const [sortBy, setSortBy] = useState(SORT_RECENT);
    const [searchInput, setSearchInput] = useState('');
    const [viewMode, setViewMode] = useState(readViewMode);
    const isCompact = viewMode === VIEW_COMPACT;

    const onViewModeChange = useCallback((mode) => {
        if (mode !== VIEW_COMPACT && mode !== VIEW_FULL) return;
        setViewMode(mode);
        writeViewMode(mode);
    }, []);

    useEffect(() => {
        if (isAuthLoading) return;
        refreshBuilds();
    }, [isAuthLoading, refreshBuilds]);

    const fetchLikedBuilds = useCallback(async () => {
        if (!isAuthenticated || isAuthLoading) {
            setLikedBuilds([]);
            setLikedLoadFailed(false);
            return;
        }
        setLikedLoading(true);
        setLikedLoadFailed(false);
        try {
            const pageSize = 100;
            let page = 1;
            let total = 0;
            const rows = [];
            do {
                const data = await listLikedCommunityBuilds({ sort: 'recent', page, page_size: pageSize });
                const results = Array.isArray(data?.results) ? data.results : [];
                rows.push(...results);
                total = Number(data?.total || rows.length);
                page += 1;
            } while (rows.length < total && page <= 20);
            setLikedBuilds(rows);
        } catch {
            setLikedBuilds([]);
            setLikedLoadFailed(true);
        } finally {
            setLikedLoading(false);
        }
    }, [isAuthLoading, isAuthenticated]);

    useEffect(() => {
        if (activeTab !== TAB_LIKED) return;
        fetchLikedBuilds();
    }, [activeTab, fetchLikedBuilds]);

    useEffect(() => {
        setSelectedClasses(new Set());
        setSelectedTags(new Set());
        setSearchInput('');
    }, [activeTab]);

    const activeRows = useMemo(
        () => (activeTab === TAB_LIKED ? likedBuilds : visibleBuilds),
        [activeTab, likedBuilds, visibleBuilds]
    );

    const allTags = useMemo(() => {
        const tagSet = new Set();
        activeRows.forEach(b => {
            const tags = normTagIds(b.build?.criteriaTags);
            tags.forEach(t => tagSet.add(t));
        });

        const dmgEls = ['neutral', 'earth', 'fire', 'air', 'water', 'push'];
        const getTagOrder = (tag) => {
            if (tag.startsWith('dmg:')) {
                const el = tag.slice(4);
                const idx = dmgEls.indexOf(el);
                if (idx !== -1) return idx;
            }
            return 999;
        };

        return Array.from(tagSet).sort((a, b) => {
            const orderA = getTagOrder(a);
            const orderB = getTagOrder(b);
            if (orderA !== orderB) return orderA - orderB;
            return a.localeCompare(b);
        });
    }, [activeRows]);

    const toggleClass = (clsName) => {
        setSelectedClasses(prev => {
            const next = new Set(prev);
            if (next.has(clsName)) next.delete(clsName);
            else next.add(clsName);
            return next;
        });
    };

    const toggleTag = (tagId) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(tagId)) next.delete(tagId);
            else next.add(tagId);
            return next;
        });
    };

    const filteredBuilds = useMemo(() => {
        const q = searchInput.trim().toLowerCase();
        const sorted = [...activeRows].sort((a, b) => compareBuilds(a, b, sortBy));
        return sorted.filter(entry => {
            const b = entry.build;
            if (q && !(entry?.name || '').toLowerCase().includes(q)) return false;
            if (selectedClasses.size > 0) {
                const clsName = b?.characterClass?.name;
                if (!clsName || !selectedClasses.has(clsName)) return false;
            }
            if (selectedTags.size > 0) {
                const tags = normTagIds(b?.criteriaTags);
                for (const t of selectedTags) {
                    if (!tags.includes(t)) return false;
                }
            }
            return true;
        });
    }, [activeRows, selectedClasses, selectedTags, sortBy, searchInput]);

    const handleOpen = async (id) => {
        const entry = await openBuild(id);
        if (!entry) return;
        navigate(getBuildPath(entry, lang) || generatorHref(lang));
    };

    const handleCreate = async () => {
        const entry = await createBuild();
        if (!entry) return;
        navigate(getBuildPath(entry, lang) || generatorHref(lang), {
            state: { focusBuildName: true },
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm(getTxt(MSG_CONFIRM_DELETE, lang))) return;
        await deleteBuild(id);
        showNotification(getTxt(MSG_BUILD_DELETED, lang), NOTIFICATION.SUCCESS);
    };

    const handleDuplicate = async (id) => {
        const copied = await duplicateBuild(id);
        if (!copied) return;
        showNotification(getTxt(MSG_BUILD_COPIED, lang), NOTIFICATION.SUCCESS);
        navigate(getBuildPath(copied, lang) || generatorHref(lang));
    };

    const handleLibraryLike = async (id, hadLiked) => {
        if (!isAuthenticated) {
            showNotification(TXT_LOG_IN_TO_LIKE[lang] || TXT_LOG_IN_TO_LIKE.en, NOTIFICATION.INFO);
            return;
        }
        patchBuildsEntry(id, (row) => ({
            ...row,
            viewer: { ...(row.viewer || {}), hasLiked: !hadLiked },
            metrics: { ...(row.metrics || {}), likes: Math.max(0, Number(row?.metrics?.likes || 0) + (hadLiked ? -1 : 1)) },
        }));
        try {
            const res = await toggleCommunityBuildLike(id);
            patchBuildsEntry(id, (row) => ({
                ...row,
                viewer: { ...(row.viewer || {}), hasLiked: !!res.liked },
                metrics: { ...(row.metrics || {}), likes: Number(res.likes || 0) },
            }));
        } catch {
            patchBuildsEntry(id, (row) => ({
                ...row,
                viewer: { ...(row.viewer || {}), hasLiked: hadLiked },
                metrics: { ...(row.metrics || {}), likes: Math.max(0, Number(row?.metrics?.likes || 0) + (hadLiked ? 1 : -1)) },
            }));
            showNotification(getTxt(MSG_LIKE_FAILED, lang), NOTIFICATION.ERROR);
        }
    };

    const handleLikedToggle = async (id, hadLiked) => {
        setLikedBuilds((prev) => prev.map((row) => (
            row.id === id
                ? {
                    ...row,
                    viewer: { ...(row.viewer || {}), hasLiked: !hadLiked },
                    metrics: { ...(row.metrics || {}), likes: Math.max(0, Number(row?.metrics?.likes || 0) + (hadLiked ? -1 : 1)) },
                }
                : row
        )));

        try {
            const res = await toggleCommunityBuildLike(id);
            if (!res?.liked) {
                setLikedBuilds((prev) => prev.filter((row) => row.id !== id));
                return;
            }
            setLikedBuilds((prev) => prev.map((row) => (
                row.id === id
                    ? {
                        ...row,
                        viewer: { ...(row.viewer || {}), hasLiked: true },
                        metrics: { ...(row.metrics || {}), likes: Number(res.likes || 0) },
                    }
                    : row
            )));
        } catch {
            setLikedBuilds((prev) => prev.map((row) => (
                row.id === id
                    ? {
                        ...row,
                        viewer: { ...(row.viewer || {}), hasLiked: hadLiked },
                        metrics: { ...(row.metrics || {}), likes: Math.max(0, Number(row?.metrics?.likes || 0) + (hadLiked ? 1 : -1)) },
                    }
                    : row
            )));
            showNotification(getTxt(MSG_LIKE_FAILED, lang), NOTIFICATION.ERROR);
        }
    };

    const showFilters = activeTab === TAB_CREATED
        ? (!isAuthLoading && !accountBuildsUnavailable && visibleBuilds.length > 0)
        : (isAuthenticated && !likedLoadFailed && (likedLoading || likedBuilds.length > 0));

    const gridClass = `mybuilds-grid${isCompact ? ' mybuilds-grid--compact' : ''}`;

    // Rendered inside BuildListHeader's title slot when the filters show, so the
    // tabs share one row with the count / sort / search controls.
    const tabs = (
        <Tabs
            ariaLabel={TXT_MY_BUILDS_TABS[lang] || TXT_MY_BUILDS_TABS.en}
            className="mybuilds-tabs"
            value={activeTab}
            onChange={setActiveTab}
            items={[
                { value: TAB_CREATED, label: getTxt(TXT_CREATED_TAB, lang) },
                { value: TAB_LIKED, label: getTxt(TXT_LIKED_TAB, lang) },
            ]}
        />
    );

    return (
        <PageTemplate
            header={(
                <div className="mybuilds-header">
                    <h1 className="page-template-title">{getTxt(TXT_TITLE, lang)}</h1>
                    {/* Only once auth has resolved as anonymous: rendering it while
                        `loading` would flash "you are not logged in" at every
                        logged-in visitor on every page load. */}
                    {authState === 'anonymous' && (
                        <div className="mybuilds-guest-note">
                            <p className="mybuilds-guest-note-text">{getTxt(TXT_GUEST_NOTICE, lang)}</p>
                            {/* No `next`: buildDiscordLoginUrl falls back to the
                                current path, so the round trip lands back here. */}
                            <button type="button" className="primary alt" onClick={() => startLogin()}>
                                {getTxt(TXT_GUEST_LOGIN, lang)}
                            </button>
                        </div>
                    )}
                </div>
            )}
        >
            <div className="mybuilds-container">
                {!showFilters && tabs}
                {showFilters && (
                    <BuildListHeader
                        lang={lang}
                        title={tabs}
                        classes={CLASSES}
                        selectedClasses={selectedClasses}
                        onToggleClass={toggleClass}
                        tagIds={allTags}
                        selectedTags={selectedTags}
                        onToggleTag={toggleTag}
                        searchValue={searchInput}
                        onSearchChange={setSearchInput}
                        searchPlaceholder={getTxt(TXT_SEARCH, lang)}
                        leading={<p className="build-list-count">{filteredBuilds.length} {getTxt(TXT_RESULTS, lang)}</p>}
                        sortValue={sortBy}
                        onSortChange={setSortBy}
                        sortLabel={getTxt(TXT_SORT_LABEL, lang)}
                        sortOptions={[
                            { value: SORT_LIKES, label: getTxt(TXT_SORT_LIKES, lang) },
                            { value: SORT_VIEWS, label: getTxt(TXT_SORT_VIEWS, lang) },
                            { value: SORT_RECENT, label: getTxt(TXT_SORT_RECENT, lang) },
                        ]}
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        viewModeLabel={getTxt(TXT_VIEW_MODE, lang)}
                    />
                )}

                {activeTab === TAB_LIKED ? (
                    !isAuthenticated ? (
                        <div className="mybuilds-empty"><p>{getTxt(TXT_LIKED_LOGIN, lang)}</p></div>
                    ) : likedLoading ? (
                        <div className="mybuilds-empty"><p>{getTxt(TXT_LIKED_LOADING, lang)}</p></div>
                    ) : likedLoadFailed ? (
                        <div className="mybuilds-empty">
                            <p>{getTxt(TXT_LIKED_UNAVAILABLE, lang)}</p>
                            <button className="mybuilds-create-btn btn-plain" onClick={fetchLikedBuilds}>{getTxt(TXT_RETRY, lang)}</button>
                        </div>
                    ) : likedBuilds.length === 0 ? (
                        <div className="mybuilds-empty"><p>{getTxt(TXT_LIKED_EMPTY, lang)}</p></div>
                    ) : filteredBuilds.length === 0 ? (
                        <div className="mybuilds-empty"><p>{getTxt(TXT_FILTER_EMPTY, lang)}</p></div>
                    ) : (
                        <div className={gridClass}>
                            {filteredBuilds.map((entry) => (
                                isCompact ? (
                                    <CompactBuildCard
                                        key={entry.id}
                                        entry={entry}
                                        lang={lang}
                                        onToggleLike={handleLikedToggle}
                                    />
                                ) : (
                                    <CommunityBuildCard
                                        key={entry.id}
                                        entry={entry}
                                        lang={lang}
                                        onToggleLike={handleLikedToggle}
                                    />
                                )
                            ))}
                        </div>
                    )
                ) : isAuthLoading ? (
                    <div className="mybuilds-empty"><p>{getTxt(TXT_LOADING, lang)}</p></div>
                ) : accountBuildsUnavailable ? (
                    <div className="mybuilds-empty">
                        <p>{getTxt(TXT_ACCOUNT_UNAVAILABLE, lang)}</p>
                        <button className="mybuilds-create-btn btn-plain" onClick={refreshBuilds}>{getTxt(TXT_RETRY, lang)}</button>
                    </div>
                ) : visibleBuilds.length === 0 ? (
                    <div className="mybuilds-empty">
                        <p>{getTxt(TXT_EMPTY, lang)}</p>
                        <button className="mybuilds-create-btn btn-plain" onClick={handleCreate}>
                            <Add fontSize="small" />
                            {getTxt(TXT_CREATE, lang)}
                        </button>
                    </div>
                ) : filteredBuilds.length === 0 ? (
                    <div className="mybuilds-empty">
                        <p>{getTxt(TXT_FILTER_EMPTY, lang)}</p>
                    </div>
                ) : (
                    <div className={gridClass}>
                        <button
                            type="button"
                            className="mybuilds-create-tile"
                            onClick={handleCreate}
                            aria-label={getTxt(TXT_CREATE, lang)}
                        >
                            <span className="mybuilds-create-tile-plus" aria-hidden="true">
                                <Add fontSize="inherit" />
                            </span>
                            <span className="mybuilds-create-tile-label">{getTxt(TXT_CREATE, lang)}</span>
                        </button>
                        {filteredBuilds.map(entry => (
                            isCompact ? (
                                <CompactBuildCard
                                    key={entry.id}
                                    entry={entry}
                                    variant="owned"
                                    isActive={entry.id === activeBuildId}
                                    lang={lang}
                                    onActivate={() => handleOpen(entry.id)}
                                    onDelete={handleDelete}
                                    onDuplicate={handleDuplicate}
                                    onToggleLike={isAuthenticated && entry.scope === 'remote' ? handleLibraryLike : undefined}
                                />
                            ) : (
                                <LibraryBuildCard
                                    key={entry.id}
                                    entry={entry}
                                    isActive={entry.id === activeBuildId}
                                    lang={lang}
                                    onActivate={() => handleOpen(entry.id)}
                                    onDelete={handleDelete}
                                    onDuplicate={handleDuplicate}
                                    onToggleLike={isAuthenticated && entry.scope === 'remote' ? handleLibraryLike : undefined}
                                />
                            )
                        ))}
                    </div>
                )}
            </div>
        </PageTemplate>
    );
};

export default MyBuilds;
